import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Search, Plus, Download, Upload, X, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Pager from '@/components/Pager';
import { NET_COLLECTIONS, NET_STATUSES } from './collections';
import NetworkContactForm from './NetworkContactForm';
import NetworkContactSheet from './NetworkContactSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 25;

const Select = ({ value, onChange, options, placeholder, testid }) => (
    <select
        className="h-9 rounded-md border border-input bg-background px-2.5 text-sm max-w-[170px]"
        value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
    >
        <option value="">{placeholder}</option>
        {options.map(o => typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

export default function NetworkAdmin({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [meta, setMeta] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ collection: '', country: '', continent: '', priority: '', status_relation: '', tag: '', q: '' });
    const [selected, setSelected] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [sheetId, setSheetId] = useState(null);
    const [importing, setImporting] = useState(false);
    const fileRef = useRef(null);
    const searchTimer = useRef(null);

    const loadMeta = useCallback(async () => {
        try { setMeta((await api.get('/network/meta')).data); } catch { /* table absente */ }
    }, [api]);

    const load = useCallback(async (p = 1, f = filters) => {
        setLoading(true);
        try {
            const params = { page: p, per_page: PER_PAGE };
            Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
            const r = await api.get('/network/contacts', { params });
            setItems(r.data.items); setTotal(r.data.total); setPage(p);
            setSelected([]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement impossible — la table network_contacts existe-t-elle ?');
        } finally {
            setLoading(false);
        }
    }, [api, filters]);

    useEffect(() => { loadMeta(); load(1); /* eslint-disable-next-line */ }, []);

    const applyFilter = (k, v) => {
        const f = { ...filters, [k]: v };
        setFilters(f);
        if (k === 'q') {
            clearTimeout(searchTimer.current);
            searchTimer.current = setTimeout(() => load(1, f), 350);
        } else {
            load(1, f);
        }
    };

    const resetFilters = () => {
        const f = { collection: '', country: '', continent: '', priority: '', status_relation: '', tag: '', q: '' };
        setFilters(f); load(1, f);
    };

    const hasFilter = Object.values(filters).some(Boolean);

    const download = async (fmt) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const r = await api.get(`/network/export.${fmt}`, { params, responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = `reseau_african_valley.${fmt}`;
            a.click(); URL.revokeObjectURL(url);
        } catch { toast.error('Export impossible'); }
    };

    const onImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/network/import', fd);
            toast.success(`${r.data.imported} contacts importés/mis à jour`);
            loadMeta(); load(1);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Import échoué');
        } finally {
            setImporting(false);
        }
    };

    const bulkSet = async (set) => {
        try {
            const r = await api.post('/network/contacts/bulk', { ids: selected, set });
            toast.success(`${r.data.updated} contacts modifiés`);
            load(page);
        } catch { toast.error('Édition en masse échouée'); }
    };

    const bulkAddTag = () => {
        const tag = window.prompt('Tag à ajouter aux contacts sélectionnés :');
        if (tag?.trim()) bulkSet({ add_tags: [tag.trim()] });
    };

    const toggleAll = (checked) => setSelected(checked ? items.map(i => i.id) : []);
    const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    return (
        <div className="space-y-6" data-testid="network-admin">
            {/* Chips collections */}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => applyFilter('collection', '')}
                    data-testid="network-chip-all"
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${!filters.collection ? 'bg-primary text-primary-foreground border-primary' : 'bg-surface border-border hover:bg-surface-muted'}`}
                >
                    Tous · {meta?.total ?? '…'}
                </button>
                {Object.entries(NET_COLLECTIONS).map(([k, m]) => (
                    <button
                        key={k} type="button"
                        onClick={() => applyFilter('collection', filters.collection === k ? '' : k)}
                        data-testid={`network-chip-${k}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filters.collection === k ? 'bg-primary text-primary-foreground border-primary' : `${m.color} hover:opacity-80`}`}
                    >
                        <m.icon size={12} strokeWidth={1.8} /> {m.label} · {meta?.collections?.[k] ?? 0}
                    </button>
                ))}
            </div>

            {/* Barre actions + recherche */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                    <Input
                        className="pl-9"
                        placeholder="Recherche globale : nom, organisation, email, fonction, pays…"
                        value={filters.q}
                        onChange={e => applyFilter('q', e.target.value)}
                        data-testid="network-search"
                    />
                </div>
                <Select value={filters.country} onChange={v => applyFilter('country', v)} options={meta?.countries || []} placeholder="Pays" testid="network-filter-country" />
                <Select value={filters.continent} onChange={v => applyFilter('continent', v)} options={meta?.continents || []} placeholder="Continent" testid="network-filter-continent" />
                <Select value={filters.status_relation} onChange={v => applyFilter('status_relation', v)}
                        options={Object.entries(NET_STATUSES).map(([value, m]) => ({ value, label: m.label }))}
                        placeholder="Statut" testid="network-filter-status" />
                <Select value={filters.priority} onChange={v => applyFilter('priority', v)} options={['P1', 'P2', 'P3', 'Haute', 'Moyenne', 'Basse']} placeholder="Priorité" testid="network-filter-priority" />
                {(meta?.tags?.length || 0) > 0 && (
                    <Select value={filters.tag} onChange={v => applyFilter('tag', v)} options={meta.tags} placeholder="Tag" testid="network-filter-tag" />
                )}
                {hasFilter && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="network-filters-reset">
                        <X size={13} className="mr-1" /> Réinitialiser
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => { setEditContact(null); setFormOpen(true); }} data-testid="network-add-btn">
                    <Plus size={15} className="mr-1.5" /> Ajouter un contact
                </Button>
                <Button variant="outline" onClick={() => download('csv')} data-testid="network-export-csv">
                    <Download size={14} className="mr-1.5" /> CSV
                </Button>
                <Button variant="outline" onClick={() => download('xlsx')} data-testid="network-export-xlsx">
                    <Download size={14} className="mr-1.5" /> Excel
                </Button>
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="network-import-btn">
                    {importing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                    Importer Excel
                </Button>
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onImportFile} data-testid="network-import-input" />
                <div className="ml-auto text-sm text-foreground/60" data-testid="network-total">
                    {total} contact{total > 1 ? 's' : ''}
                </div>
            </div>

            {/* Barre d'édition en masse */}
            {selected.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary-light/40 px-4 py-2.5" data-testid="network-bulk-bar">
                    <span className="text-sm font-medium">{selected.length} sélectionné{selected.length > 1 ? 's' : ''}</span>
                    <Select value="" onChange={v => v && bulkSet({ status_relation: v })}
                            options={Object.entries(NET_STATUSES).map(([value, m]) => ({ value, label: m.label }))}
                            placeholder="→ Statut" testid="network-bulk-status" />
                    <Select value="" onChange={v => v && bulkSet({ priority: v })} options={['P1', 'P2', 'P3', 'Haute', 'Moyenne', 'Basse']} placeholder="→ Priorité" testid="network-bulk-priority" />
                    <Button size="sm" variant="outline" onClick={bulkAddTag} data-testid="network-bulk-tag">
                        <Tags size={13} className="mr-1.5" /> Ajouter un tag
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Annuler</Button>
                </div>
            )}

            {/* Table */}
            <div className="rounded-xl border border-border overflow-x-auto bg-surface">
                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" /></div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16 text-foreground/50" data-testid="network-empty">
                        {meta?.total === 0
                            ? 'Base vide — importez votre fichier Excel (9 onglets) ou ajoutez un contact.'
                            : 'Aucun contact ne correspond à ces filtres.'}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-foreground/50">
                                <th className="px-3 py-2.5 w-8">
                                    <input type="checkbox" checked={selected.length === items.length && items.length > 0}
                                           onChange={e => toggleAll(e.target.checked)} data-testid="network-select-all" />
                                </th>
                                <th className="px-3 py-2.5">Nom</th>
                                <th className="px-3 py-2.5">Fonction / Organisation</th>
                                <th className="px-3 py-2.5">Email</th>
                                <th className="px-3 py-2.5">Collection</th>
                                <th className="px-3 py-2.5">Pays</th>
                                <th className="px-3 py-2.5 text-right">Score</th>
                                <th className="px-3 py-2.5">Priorité</th>
                                <th className="px-3 py-2.5">Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(c => {
                                const m = NET_COLLECTIONS[c.collection];
                                const st = NET_STATUSES[c.status_relation];
                                return (
                                    <tr key={c.id}
                                        className="border-b border-border/60 last:border-0 hover:bg-surface-muted cursor-pointer transition-colors"
                                        onClick={() => setSheetId(c.id)}
                                        data-testid="network-row"
                                    >
                                        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="font-medium">{c.full_name}</div>
                                        </td>
                                        <td className="px-3 py-2.5 max-w-[240px]">
                                            <div className="truncate">{c.role_function || '—'}</div>
                                            {c.organization && <div className="text-[11px] text-foreground/50 truncate">{c.organization}</div>}
                                        </td>
                                        <td className="px-3 py-2.5 max-w-[200px]" onClick={e => e.stopPropagation()} data-testid="network-col-email">
                                            {c.email
                                                ? <a href={`mailto:${c.email}`} className="text-primary hover:underline truncate block max-w-[200px]">{c.email}</a>
                                                : <span className="text-foreground/40">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {m && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${m.color}`}><m.icon size={10} /> {m.label}</span>}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">{c.country || '—'}</td>
                                        <td className="px-3 py-2.5 text-right font-medium tabular-nums">{c.score_total != null ? Math.round(c.score_total) : '—'}</td>
                                        <td className="px-3 py-2.5">{c.priority || '—'}</td>
                                        <td className="px-3 py-2.5">
                                            {st && <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${st.color}`}>{st.label}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {total > PER_PAGE && (
                <Pager page={page} total={total} pageSize={PER_PAGE} onPage={p => load(p)} testid="network-pager" />
            )}

            <NetworkContactForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSaved={() => { loadMeta(); load(page); if (sheetId) setSheetId(null); }}
                api={api}
                contact={editContact}
            />
            {sheetId && (
                <NetworkContactSheet
                    contactId={sheetId}
                    onClose={() => setSheetId(null)}
                    onEdit={(c) => { setEditContact(c); setFormOpen(true); }}
                    onDeleted={() => { loadMeta(); load(page); }}
                    api={api}
                />
            )}
        </div>
    );
}
